import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { showNotification } from "jazasoft";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSetting, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity } from "../../../utils/types";
import { isEmpty, isTrue } from "../../../utils/helpers";
import { UPDATE_TASK } from "../../../reducers/scheduledTaskReducer";
import { getCutOrderRows } from "../../cutplan/cutPlanHelper";
import { getGroupName } from "../../grouping/groupingHelper";
import { parseMarkerName } from "../../../helpers/orderHelper";

const useLayslip = props => {
  const id = props.id;
  const routeState = props.location?.state;

  const [version, setVersion] = React.useState(0);
  const [layslipVersion, setLayslipVersion] = React.useState(0);
  const [selectedKey, setSelectedKey] = React.useState(null);
  const [layTabIdx, setLayTabIdx] = React.useState(0);
  const [dialogState, setDialogState] = React.useState({ open: false, lay: null });

  const dispatch = useDispatch();
  const { allUnitList } = useUnit();

  const [mutate] = useMutation();
  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);
  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);
  const layFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.LAYS);
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const messages = useSelector(state => state?.i18n?.messages || {});

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" }, version });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList)) return [];
    return getCutOrderRows(order, productParts);
  }, [order, productParts]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "fpoQty", title: "FPO Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      // { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }

    return cols;
  }, [flowInfoRows, allUnitList]);

  const orderFlow = flowInfoRows.find(fi => fi.key === selectedKey);
  const cutOrder = orderFlow?.cutOrder || {};

  const { data: lays = [] } = useQuery(!isEmpty(cutOrder) && { url: `lays`, params: { search: `orderFlow.id==${cutOrder.id}` }, layslipVersion });
  const { data: layRowData = [] } = useQuery(!isEmpty(cutOrder) && { url: "lay-rows", params: { search: `lay.orderFlow.id==${cutOrder?.id}` } });
  const { data: rollData = [] } = useQuery(
    !isEmpty(cutOrder) && {
      url: "rolls",
      params: {
        search: `cutOrderFlow.id==${cutOrder?.id};(hasChild=='NULL',hasChild=='FALSE');(active=='NULL',active=='TRUE',active=='FALSE')`,
        size: 10000
      }
    }
  );
  const rollList = rollData?.filter(e => isTrue(e.active)) || [];

  const layList = useDeepMemo(() => {
    const layList = lays
      .sort((a, b) => a.layNo - b.layNo)
      .map(lay => {
        const marker = cutOrder.markerList?.find(m => m.id === lay.markerId) || {};
        const status = lay.state;

        const group = cutOrder?.groupList?.find(g => marker.groupId === g.id);
        const { sizeGroup, name } = parseMarkerName(marker.name);
        return {
          ...lay,
          marker,
          group,
          layNo: lay.layNo,
          sizeGroup,
          markerName: name,
          way: marker.noOfWays,
          layLength: marker.layLength,
          groupName: getGroupName(group),
          status: status
        };
      });
    // .filter(lay =>
    //   layTabIdx === 0
    //     ? lay.marker.markerType === "FINAL"
    //     : layTabIdx === 1
    //     ? lay.marker.markerType === "REMNANT"
    //     : layTabIdx === 2
    //     ? lay.marker.markerType === "RE_CUTTING"
    //     : false
    // )
    return layList;
  }, [lays, cutOrder, layTabIdx]);

  const layRowList = useDeepMemo(() => {
    if (isEmpty(cutOrder) || isEmpty(layRowData)) return [];
    const layRowList = layRowData.map(row => ({
      ...row,
      rollInfo: rollData?.find(r => r.id === row.rollInfoId),
      marker: cutOrder.markerList?.find(m => m.id === row.markerId)
    }));

    return layRowList;
  }, [layRowData, cutOrder, rollData]);

  useDeepEffect(() => {
    if (!isEmpty(routeState?.selectedKey)) {
      setSelectedKey(routeState?.selectedKey);
    } else if (!isEmpty(flowInfoRows)) {
      setSelectedKey(flowInfoRows[0].key);
    }
  }, [flowInfoRows.length, routeState]);

  useDeepEffect(() => {
    if (!isEmpty(routeState?.layTabIdx)) {
      setLayTabIdx(routeState.layTabIdx);
    }
  }, [routeState]);

  const reGenerateRemnantMarkers = foundRolls => {
    if (isEmpty(order?.flowInfoList) || isEmpty(cutOrder)) return;

    // const flowInfoList = order.flowInfoList.filter(
    //   fi =>
    //     listFromCsv(orderFlow.styles).includes(fi.style) &&
    //     listFromCsv(orderFlow.colors).includes(fi.color) &&
    //     fi.product?.id === orderFlow.product?.id &&
    //     fi.part === orderFlow.part
    // );

    // const hasProductRatio = flowInfoList.filter(fi => !isEmpty(fi.productRatio)).length > 1;
    // const productIds = hasProductRatio ? flowInfoList.filter(fi => !isEmpty(fi.productRatio)).map(fi => fi.product?.id) : [orderFlow.product?.id];
    // const flowList = order.orderFlowList
    //   ?.filter(of => of.styles === orderFlow?.styles && of.colors === orderFlow?.colors && productIds.includes(of.product?.id))
    //   ?.filter(of => of.stateValue > OState.NEW.stateValue);

    // const flowIds = isEmpty(flowList) ? [orderFlow?.id] : flowList.map(e => e.id);
    // const noOfLots = flowInfoList.sort((a, b) => b.lots - a.lots)[0]?.lots || 1;
    // const partCount = getDistinctValues(flowInfoList.map(e => e.part)).length;

    const planningDto = {
      //   flowIds: flowIds.join(","),
      cutOrderId: cutOrder?.id,
      poRef: order?.poRef,
      colors: cutOrder?.colors,
      styles: cutOrder?.styles,
      inseams: cutOrder?.inseams,
      destinations: cutOrder?.destinations,
      deliveries: cutOrder?.deliveries,
      flowRefs: cutOrder?.flowRefs,
      customerFlowRefs: cutOrder?.customerFlowRefs,
      productName: cutOrder?.product?.name,
      part: cutOrder?.part,
      lot: cutOrder?.lot,
      //   noOfLots,
      //   partCount,
      foundRolls
    };

    const options = {
      url: `orders/${id}/markers`,
      method: "post",
      data: planningDto,
      params: { flowId: cutOrder.id, action: "re_generate" }
    };

    mutate(options, {
      onSuccess: task => {
        dispatch({ type: UPDATE_TASK, payload: { task } });
        dispatch(showNotification("Task queued. you will be notified once completed"));
      }
    });
  };

  const onRefresh = () => {
    setVersion(v => v + 1);
    setLayslipVersion(v => v + 1);
  };

  return {
    version,
    orderFields,
    flowInfoFields,
    rollFields,
    layFields,
    messages,
    order,
    flowInfoColumns,
    flowInfoRows,
    cutOrder,
    rollList,
    layRowList,
    layList,
    layTabIdx,
    selectedKey,
    dialogState,
    mutate,
    setLayslipVersion,
    setDialogState,
    setSelectedKey,
    setLayTabIdx,
    reGenerateRemnantMarkers,
    onRefresh,
    allUnitList
  };
};

export default useLayslip;
